import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { faUser } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Box, Flex, Input, Text, ValidationMessage } from '@modusign/design-system';
import { useHosts } from '@modusign/react-signing';
import { SignerToReference, SigningMethod } from '@modusign/react-signing/types';
import { removeAllDash } from 'libs/formatter/removeAllDash';
import { removeAllSpace } from 'libs/formatter/removeAllSpace';
import { isNumberOnly } from 'libs/validation/isNumberOnly';
import debounce from 'lodash/debounce';
import { useEffect, useMemo } from 'react';
import { useController, useFormContext } from 'react-hook-form';
import { useSelector } from 'react-redux';
import { useUnmount } from 'react-use';
import isMobilePhone from 'validator/lib/isMobilePhone';

import { CONTACT_INFO_MAX_LENGTH } from '@app/constants/contactInfoMaxLength';
import { duplicatedContactInfoValidator } from '@app/features/apply-react-signing/libs/validations/checkDuplicatedContactInfo';
import { getFormattedSouthKoreaPhoneNumber } from '@app/libs/inputFormat';
import { validateEmail } from '@app/libs/validation';
import { getEmail } from '@app/store/user/user/selectors';

import SigningMethodSelect from '../SignerFormContactAndMethodField/SigningMethodSelect/index';

const isPhoneNumber = (v: string) => isNumberOnly(removeAllSpace(removeAllDash(v)));

function phoneNumberValidator(value: string) {
  const v = removeAllSpace(removeAllDash(value));

  if (!v) {
    return '번호를 입력해주세요.';
  }

  if (v.length < 11) {
    return '11자리 번호를 입력해 주세요.';
  }

  if (!v.startsWith('010')) {
    return '010 번호만 입력할 수 있습니다.';
  }

  if (!isMobilePhone(v, 'ko-KR')) {
    return '정확한 휴대폰 번호를 입력해 주세요.';
  }

  return true;
}

function inPersonSignContactInfoFormatValidator(value: string) {
  if (isPhoneNumber(value)) {
    return phoneNumberValidator(value);
  }
  return validateEmail(value).success || validateEmail(value).message;
}

export default function InPersonContactInfoForm({
  required,
  signerId,
  order,
  setUserAsDefaultHost,
  onSingingMethodChange,
  onContactReset,
}: {
  required: boolean;
  signerId: string;
  order: number;
  setUserAsDefaultHost?: boolean;
  onSingingMethodChange: (signingMethod: SigningMethod) => void;
  onContactReset: () => void;
}) {
  const hostDataLabelInputName = `${signerId}.host.participantId`;

  const currentUserEmail = useSelector(getEmail);
  const { hosts } = useHosts();

  const defaultHost = useMemo(
    () => hosts.find((h) => h.contact === currentUserEmail),
    [currentUserEmail, hosts]
  );

  const { control, errors, getValues, trigger, register, setValue, unregister } =
    useFormContext<Record<string, SignerToReference>>();
  const {
    field: { value, onChange, ref, onBlur },
  } = useController({
    control,
    name: `${signerId}.contact`,
    defaultValue: '',
    rules: {
      required: required ? '이메일 주소 또는 휴대폰 번호를 입력해주세요.' : undefined,
      validate: {
        isInvalid: inPersonSignContactInfoFormatValidator,
        isDuplicated: (targetContact: string) =>
          duplicatedContactInfoValidator({
            signers: getValues(),
            signerId,
            order,
            targetContact,
            errors,
          }),
      },
      setValueAs: (v: string) => {
        if (isPhoneNumber(v)) {
          return v.replace(/\D/g, '');
        }
        return v;
      },
    },
  });

  const error = errors[signerId]?.contact?.message || '';

  const formatContactInfo = (value: string) => {
    if (phoneNumberValidator(value) === true) {
      return getFormattedSouthKoreaPhoneNumber(value);
    }

    return value;
  };

  useUnmount(() => {
    unregister(hostDataLabelInputName);
  });

  useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore // react-hook-form setValue의 타입 개선이 필요 (v7에서는 개선)
    setValue(hostDataLabelInputName, defaultHost?.id);
  }, [defaultHost, hostDataLabelInputName, setValue]);

  const debounceTrigger = debounce(() => {
    trigger();
  }, 600);

  return (
    <Box>
      <Flex>
        <Box width="80px">
          <SigningMethodSelect
            value={'IN_PERSON'}
            onSelect={(selectedMethod) => {
              onSingingMethodChange(selectedMethod);
            }}
          />
        </Box>
        <Box sx={{ position: 'relative' }} width="100%">
          <Input
            autoFocus
            isInvalid={!!error}
            maxLength={CONTACT_INFO_MAX_LENGTH}
            placeholder="이메일 주소 또는 휴대폰 번호"
            ref={ref}
            sx={{
              paddingRight: 8,
            }}
            value={formatContactInfo(value)}
            onBlur={onBlur}
            onChange={(e) => {
              onChange(e.target.value);
              debounceTrigger();
            }}
          />

          {value && (
            <button
              aria-label="clear"
              className="InputSet__clear-button"
              type="button"
              onClick={onContactReset}
            />
          )}
        </Box>
      </Flex>
      {error ? (
        <ValidationMessage fontSize="sm" fontWeight="regular">
          {error}
        </ValidationMessage>
      ) : (
        <Box mt="3px">
          <Text color="textNeutralLight" fontSize="sm" fontWeight="regular">
            대면서명자의 연락처를 입력해 주세요.
          </Text>
        </Box>
      )}
      <Flex
        alignItems="center"
        bg="notificationBackgroundNeutralBase"
        borderRadius="3px"
        mt={3}
        px={5}
        py={4}
      >
        <Text fontSize="14px">
          <FontAwesomeIcon icon={faUser as IconProp} />
        </Text>
        <Box color="textNeutralLight" ml={2}>
          {defaultHost ? (
            <>
              <Text color="textNeutralSlightlyLight">{defaultHost.name}</Text>님이{' '}
            </>
          ) : (
            <>
              서명 <Text color="textNeutralSlightlyLight">요청자</Text>가{' '}
            </>
          )}
          대면서명 진행을 맡습니다.
        </Box>
        {setUserAsDefaultHost && (
          <Input
            defaultValue={defaultHost?.id}
            name={hostDataLabelInputName}
            ref={register()}
            type="hidden"
          />
        )}
      </Flex>
    </Box>
  );
}
